import type { RenderFunction } from 'vue';
import type {
  ConfiguratorBlockType,
  Store,
  User,
  Permission
} from '@headq/shared';
import type { HeadQEntity, Notification } from './entities';
import type { ObjectKeys } from '@/utils/constants';

export interface MenuItem {
  name: string;
  route: string;
  icon: RenderFunction;
  current: boolean;
}

export interface UIAction {
  name: string;
  icon?: string;
  uicon?: string;
  action: (entity?: any) => void;
  textColor?: string;
  disabled?: boolean;
  tooltip?: string;
  children?: UIAction[];
}

export enum FieldType {
  Text = 'Text',
  Password = 'Password',
  TextArea = 'TextArea',
  Number = 'Number',
  Money = 'Money',
  Image = 'Image',
  File = 'File',
  Url = 'Url',
  Boolean = 'Boolean',
  Select = 'Select',
  MultiSelect = 'MultiSelect',
  Datetime = 'Datetime',
  Date = 'Date',
  Email = 'Email',
  Button = 'Button',
  ImageSwatch = 'ImageSwatch',
  Choice = 'Choice',
  MultiChoice = 'MultiChoice',
  ComboBox = 'ComboBox',
  Checkbox = 'Checkbox',
  RichText = 'RichText',
  Calendar = 'Calendar',
  Tags = 'Tags'
}

export enum DataType {
  Text,
  Number,
  Money,
  Datetime,
  Checkmark,
  Percentage,
  Enum,
  Custom,
  Toggle,
  FromNow,
  Date
}

export interface FieldOption {
  id: string | number | boolean | null;
  name: string;
  description?: string;
  icon?: string;
  color?: string;
  price?: number;
  selected?: boolean;
}

// Admins own InputFieldConfig.
export interface InputFieldConfig {
  key: string;
  label: string;
  type: FieldType;
  editable?: boolean;
  required?: boolean;
  description?: string;
  options?: FieldOption[];
  validation?: any;
  imageType?: string;
  fileType?: string;
  formatter?: (entity: any) => string;
  placeholder?: string;
  suffix?: string;
  click?: () => void;
}

export interface DataViewAvailableFilter {
  name: string;
  key: string;
  options: FieldOption[];
}

/*
export interface DataViewFilterSelect extends DataViewAvailableFilter {
  values: string[];
  selected: string[];
}
*/

export interface DataViewFilter {
  search: string;
  selects: DataViewAvailableFilter[];
}

export interface DataTableColumnOptions {
  currency?: string;
  locale?: string;
  recurring?: boolean;
  enumMap?: Record<string, string>;
  formatter?: (entity: any, row?: Record<string, any>) => string;
}

export interface DataTableColumn {
  key: string;
  name: string;
  type: DataType;
  options?: DataTableColumnOptions;
  href?: (row: any) => string;
  action?: (row: any) => void;
  imageUrl?: string | ((row: any) => string);
  copyToClipboard?: boolean;
  external?: boolean;
  sortable?: boolean;
  icon?: string;
  color?: string;
  initialSort?: boolean;
  reverseSort?: boolean;
  align?: 'left' | 'center' | 'right';
}

export interface DataCardConfig {
  titleFormatter: (item: HeadQEntity) => string;
  descriptionFormatter: (item: HeadQEntity) => string;
  imageUrlFormatter?: (item: HeadQEntity) => string;
}

export interface DataListItemConfig extends DataCardConfig {
  columns: DataTableColumn[];
}

export type DataTableColumns = DataTableColumn[];

export interface BreadCrumbPage {
  name: string;
  href: string;
}

export interface DropDownItem<T, V> {
  id: T;
  value: V;
}

export interface StoreContext {
  activeStore?: Store;
  vatOptions?: number[];
  typeOptions?: string[];
}

interface lastAccessedStore {
  slug: string;
  timestamp: number;
}

export interface UserStore {
  user: User;
  permissions: Permission[];
  activeStore: Store;
  initialized: boolean;
  lastAccessedStores: lastAccessedStore[];
  sidebarExpanded: boolean;
  translationContext: null | ObjectKeys;
}

export interface NotificationStore {
  notifications: Notification[];
  active?: Notification;
}

export interface NavigationItem {
  name: string;
  route: string;
  icon?: string;
  uicon?: string;
  current?: boolean;
  disabled?: boolean;
  separator?: boolean;
  isRestricted?: boolean;
  paywallFeature?: string;
}

export interface Navigation extends NavigationItem {
  inHeader: boolean;
  dropDown: boolean;
  pages: NavigationItem[];
}

export interface NavigationNestedItem extends NavigationItem {
  routes: NavigationItem[];
}

export interface ConfiguratorBlockItem {
  name: string;
  icon: any;
  type: ConfiguratorBlockType;
}

export interface ProgressStep {
  name: string;
  showPreview?: boolean;
}

export interface ObjectDeclaration {
  id: string;
  name: string;
  plural: string;
  icon: string;
  route: string;
  apiPath: string;
}

export interface OnboardingTask {
  id: string;
  title: string;
  description: string;
  resolution: string;
  url: string;
}
